/**
 * Structure Validator
 *
 * Validates the overall structure of a workflow JSON:
 * - Required fields presence
 * - At least one trigger node
 * - No disconnected nodes
 * - No duplicate IDs
 */

import {
  WorkflowJSON,
  WorkflowNodeJSON,
  WorkflowEdgeJSON,
  WorkflowJSONValidationError,
  WorkflowJSONValidationWarning,
  TRIGGER_NODE_TYPES,
  WorkflowNodeType,
} from '../types';

/**
 * Result of structure validation
 */
export interface StructureValidationResult {
  errors: WorkflowJSONValidationError[];
  warnings: WorkflowJSONValidationWarning[];
}

/**
 * Validate workflow structure
 */
export function validateWorkflowStructure(
  workflow: WorkflowJSON
): StructureValidationResult {
  const errors: WorkflowJSONValidationError[] = [];
  const warnings: WorkflowJSONValidationWarning[] = [];

  // Validate required fields
  if (!workflow.playbookName || workflow.playbookName.trim() === '') {
    errors.push({
      code: 'MISSING_REQUIRED_FIELD',
      message: 'Workflow name (playbookName) is required',
      path: 'playbookName',
      suggestion: 'Add a "playbookName" field with a descriptive name for the workflow',
    });
  }

  // Validate nodes array exists
  if (!workflow.nodes || !Array.isArray(workflow.nodes)) {
    errors.push({
      code: 'MISSING_REQUIRED_FIELD',
      message: 'Nodes array is required',
      path: 'nodes',
      suggestion: 'Add a "nodes" array with at least one trigger node',
    });
    return { errors, warnings };
  }

  // Validate at least one node exists
  if (workflow.nodes.length === 0) {
    errors.push({
      code: 'MISSING_TRIGGER',
      message: 'Workflow must have at least one node',
      path: 'nodes',
      suggestion: 'Add at least one trigger node (e.g., manualTrigger, contactCreated)',
    });
    return { errors, warnings };
  }

  // Validate each node has required fields
  workflow.nodes.forEach((node, index) => {
    const nodeErrors = validateNodeRequiredFields(node, index);
    errors.push(...nodeErrors);
  });

  // Validate at least one trigger node
  const triggerNodes = workflow.nodes.filter((node) =>
    isTriggerNode(node.type)
  );

  if (triggerNodes.length === 0) {
    errors.push({
      code: 'MISSING_TRIGGER',
      message: 'Workflow must have at least one trigger node',
      path: 'nodes',
      suggestion:
        'Add a trigger node such as manualTrigger, contactCreated, webhookTrigger, or scheduleTrigger',
    });
  }

  // Validate duplicate node IDs
  const nodeIds = new Set<string>();
  workflow.nodes.forEach((node, index) => {
    if (node.id) {
      if (nodeIds.has(node.id)) {
        errors.push({
          code: 'DUPLICATE_NODE_ID',
          message: `Duplicate node ID: "${node.id}"`,
          path: `nodes[${index}].id`,
          nodeId: node.id,
          suggestion: 'Each node must have a unique ID',
        });
      }
      nodeIds.add(node.id);
    }
  });

  // Validate edges if present
  if (workflow.edges && Array.isArray(workflow.edges)) {
    // Validate duplicate edge IDs
    const edgeIds = new Set<string>();
    workflow.edges.forEach((edge, index) => {
      if (edge.id) {
        if (edgeIds.has(edge.id)) {
          errors.push({
            code: 'DUPLICATE_EDGE_ID',
            message: `Duplicate edge ID: "${edge.id}"`,
            path: `edges[${index}].id`,
            edgeId: edge.id,
            suggestion: 'Each edge must have a unique ID',
          });
        }
        edgeIds.add(edge.id);
      }
    });
  }

  // Validate connectivity (non-trigger nodes should have incoming connections)
  const connectivityResult = validateConnectivity(workflow);
  errors.push(...connectivityResult.errors);
  warnings.push(...connectivityResult.warnings);

  return { errors, warnings };
}

/**
 * Validate required fields for a single node
 */
function validateNodeRequiredFields(
  node: WorkflowNodeJSON,
  index: number
): WorkflowJSONValidationError[] {
  const errors: WorkflowJSONValidationError[] = [];

  // Validate node ID
  if (!node.id || node.id.trim() === '') {
    errors.push({
      code: 'MISSING_REQUIRED_FIELD',
      message: `Node at index ${index} is missing required "id" field`,
      path: `nodes[${index}].id`,
      suggestion: 'Add a unique "id" field to the node',
    });
  }

  // Validate node type
  if (!node.type || node.type.trim() === '') {
    errors.push({
      code: 'MISSING_REQUIRED_FIELD',
      message: `Node "${node.id || `at index ${index}`}" is missing required "type" field`,
      path: `nodes[${index}].type`,
      nodeId: node.id,
      suggestion: 'Add a "type" field specifying the node type (e.g., manualTrigger, httpRequest)',
    });
  }

  // Validate node position
  if (!node.position) {
    errors.push({
      code: 'MISSING_REQUIRED_FIELD',
      message: `Node "${node.id || `at index ${index}`}" is missing required "position" field`,
      path: `nodes[${index}].position`,
      nodeId: node.id,
      suggestion: 'Add a "position" object with "x" and "y" coordinates, or enable autoLayout',
    });
  } else {
    if (typeof node.position.x !== 'number') {
      errors.push({
        code: 'MISSING_REQUIRED_FIELD',
        message: `Node "${node.id || `at index ${index}`}" position is missing "x" coordinate`,
        path: `nodes[${index}].position.x`,
        nodeId: node.id,
        suggestion: 'Add an "x" coordinate (number) to the position object',
      });
    }
    if (typeof node.position.y !== 'number') {
      errors.push({
        code: 'MISSING_REQUIRED_FIELD',
        message: `Node "${node.id || `at index ${index}`}" position is missing "y" coordinate`,
        path: `nodes[${index}].position.y`,
        nodeId: node.id,
        suggestion: 'Add a "y" coordinate (number) to the position object',
      });
    }
  }

  // Validate node data
  if (!node.data) {
    errors.push({
      code: 'MISSING_REQUIRED_FIELD',
      message: `Node "${node.id || `at index ${index}`}" is missing required "data" field`,
      path: `nodes[${index}].data`,
      nodeId: node.id,
      suggestion: 'Add a "data" object with at least "nodeType" and "configured" fields',
    });
  } else {
    // Validate nodeType in data matches type
    if (!node.data.nodeType) {
      errors.push({
        code: 'MISSING_REQUIRED_FIELD',
        message: `Node "${node.id || `at index ${index}`}" data is missing "nodeType" field`,
        path: `nodes[${index}].data.nodeType`,
        nodeId: node.id,
        suggestion: 'Add a "nodeType" field to the data object matching the node type',
      });
    }
  }

  return errors;
}

/**
 * Check if a node type is a trigger node
 */
function isTriggerNode(nodeType: string | undefined): boolean {
  if (!nodeType) return false;
  return TRIGGER_NODE_TYPES.has(nodeType as WorkflowNodeType);
}

/**
 * Validate workflow connectivity
 */
function validateConnectivity(
  workflow: WorkflowJSON
): { errors: WorkflowJSONValidationError[]; warnings: WorkflowJSONValidationWarning[] } {
  const errors: WorkflowJSONValidationError[] = [];
  const warnings: WorkflowJSONValidationWarning[] = [];

  if (!workflow.edges || workflow.edges.length === 0) {
    // If there's only one node (trigger), that's fine
    if (workflow.nodes.length > 1) {
      warnings.push({
        code: 'NO_EDGES',
        message: 'Workflow has multiple nodes but no edges connecting them',
        suggestion: 'Add edges to connect nodes in the workflow',
      });
    }
    return { errors, warnings };
  }

  // Build a set of nodes that have incoming connections
  const nodesWithIncoming = new Set<string>();
  const nodeIds = new Set(workflow.nodes.map((n) => n.id));

  workflow.edges.forEach((edge) => {
    if (nodeIds.has(edge.target)) {
      nodesWithIncoming.add(edge.target);
    }
  });

  // Check each non-trigger node has incoming connections
  workflow.nodes.forEach((node, index) => {
    if (!isTriggerNode(node.type) && !nodesWithIncoming.has(node.id)) {
      warnings.push({
        code: 'DISCONNECTED_NODE',
        message: `Node "${node.data?.label || node.id}" has no incoming connections`,
        path: `nodes[${index}]`,
        nodeId: node.id,
        suggestion: 'Connect this node to the workflow or remove it if not needed',
      });
    }
  });

  // Check for nodes that have no outgoing connections (potential end nodes - just a warning)
  const nodesWithOutgoing = new Set<string>();
  workflow.edges.forEach((edge) => {
    if (nodeIds.has(edge.source)) {
      nodesWithOutgoing.add(edge.source);
    }
  });

  const endNodes = workflow.nodes.filter(
    (node) => !nodesWithOutgoing.has(node.id)
  );

  if (endNodes.length === 0) {
    warnings.push({
      code: 'NO_END_NODE',
      message: 'Workflow has no end nodes - all nodes have outgoing connections',
      suggestion: 'This might create an infinite loop if there are cycles',
    });
  }

  return { errors, warnings };
}

/**
 * Detect cycles in the workflow graph
 */
export function detectCycles(
  nodes: WorkflowNodeJSON[],
  edges: WorkflowEdgeJSON[]
): WorkflowJSONValidationError[] {
  const errors: WorkflowJSONValidationError[] = [];
  const visited = new Set<string>();
  const recursionStack = new Set<string>();
  const nodeMap = new Map(nodes.map((n) => [n.id, n]));

  // Build adjacency list
  const adjacencyList = new Map<string, string[]>();
  nodes.forEach((node) => {
    adjacencyList.set(node.id, []);
  });
  edges.forEach((edge) => {
    const neighbors = adjacencyList.get(edge.source) || [];
    neighbors.push(edge.target);
    adjacencyList.set(edge.source, neighbors);
  });

  function hasCycle(nodeId: string, path: string[]): boolean {
    if (recursionStack.has(nodeId)) {
      // Found cycle
      const cycleStart = path.indexOf(nodeId);
      const cyclePath = path.slice(cycleStart).concat([nodeId]);
      const cycleDescription = cyclePath
        .map((id) => {
          const node = nodeMap.get(id);
          return node?.data?.label || node?.id || id;
        })
        .join(' -> ');

      errors.push({
        code: 'CYCLE_DETECTED',
        message: `Cycle detected in workflow: ${cycleDescription}`,
        suggestion: 'Remove one of the edges to break the cycle',
      });

      return true;
    }

    if (visited.has(nodeId)) {
      return false;
    }

    visited.add(nodeId);
    recursionStack.add(nodeId);

    const neighbors = adjacencyList.get(nodeId) || [];
    for (const neighbor of neighbors) {
      if (hasCycle(neighbor, [...path, nodeId])) {
        return true;
      }
    }

    recursionStack.delete(nodeId);
    return false;
  }

  // Check for cycles starting from each node
  for (const node of nodes) {
    if (!visited.has(node.id)) {
      hasCycle(node.id, []);
    }
  }

  return errors;
}
